import type {
  DiagnosticProfile,
  TreatmentType,
} from "@src/packages/homepage/utils/costEstimator";
import {
  getMedicationLineId,
  resolveEstimateAmount,
  roundModeledEstimate,
} from "./estimator";
import type { EstimateLocationInput } from "./estimator";
import type { EstimateLineId } from "./catalog";

export type EstimateLineStatus =
  "likely-included" | "often-separate" | "optional";

export type EstimateLineMultiplier =
  "once" | "cycle" | "embryo" | "storage-year" | "transfer";

export type EstimateScenarioLine = {
  key: string;
  label: string;
  amount: number;
  status: EstimateLineStatus;
  multiplier: EstimateLineMultiplier;
  defaultSelected: boolean;
  detail: string;
};

export type EstimateScenarioSection = {
  key: string;
  title: string;
  description: string;
  lines: EstimateScenarioLine[];
};

export type EstimateScenarioInput = {
  treatment: TreatmentType;
  diagnosticProfile: DiagnosticProfile;
  medicationIntensity: "low" | "typical" | "high";
  estimateLocation: EstimateLocationInput;
};

function getMedicationFactor(intensity: "low" | "typical" | "high") {
  if (intensity === "low") return 0.72;
  if (intensity === "high") return 1.45;
  return 1;
}

export function isRetrievalTreatment(treatment: TreatmentType) {
  return (
    treatment === "egg_freezing" ||
    treatment === "embryo_freezing" ||
    treatment === "ivf" ||
    treatment === "ivf_icsi"
  );
}

export function hasEmbryoLabPath(treatment: TreatmentType) {
  return (
    treatment === "embryo_freezing" ||
    treatment === "ivf" ||
    treatment === "ivf_icsi"
  );
}

export function hasTransferPath(treatment: TreatmentType) {
  return treatment === "ivf" || treatment === "ivf_icsi" || treatment === "fet";
}

export function hasDonorOptionsPath(treatment: TreatmentType) {
  return (
    treatment === "embryo_freezing" ||
    treatment === "ivf" ||
    treatment === "ivf_icsi" ||
    treatment === "fet" ||
    treatment === "iui"
  );
}

type LineDraft = Omit<EstimateScenarioLine, "amount">;

function toLine(
  draft: LineDraft,
  amountValue: number | null,
): EstimateScenarioLine | null {
  return amountValue == null ? null : { ...draft, amount: amountValue };
}

function compactLines(
  lines: (EstimateScenarioLine | null)[],
): EstimateScenarioLine[] {
  return lines.filter((line): line is EstimateScenarioLine => line !== null);
}

export function buildEstimateScenarioSections({
  treatment,
  diagnosticProfile,
  medicationIntensity,
  estimateLocation,
}: EstimateScenarioInput): EstimateScenarioSection[] {
  const amount = (lineId: EstimateLineId): number | null => {
    const raw = resolveEstimateAmount(lineId, estimateLocation);
    return raw == null ? null : roundModeledEstimate(raw);
  };
  const sections: EstimateScenarioSection[] = [];
  const pushSection = (section: {
    key: string;
    title: string;
    description: string;
    lines: (EstimateScenarioLine | null)[];
  }) => {
    const lines = compactLines(section.lines);
    if (lines.length === 0) return;
    sections.push({
      key: section.key,
      title: section.title,
      description: section.description,
      lines,
    });
  };
  const beforeCycleLines: (EstimateScenarioLine | null)[] = [
    toLine(
      {
        key: "consult",
        label: "Consultation and financial review",
        status: "often-separate",
        multiplier: "once",
        defaultSelected: true,
        detail:
          "Cost changes if the clinic waives the consult, credits it to treatment, or requires a separate financial visit.",
      },
      amount("consult"),
    ),
  ];

  if (diagnosticProfile === "female" || diagnosticProfile === "couple") {
    beforeCycleLines.push(
      toLine(
        {
          key: "female-workup",
          label: "Hormones, AMH, and baseline ultrasound",
          status: "often-separate",
          multiplier: "once",
          defaultSelected: true,
          detail:
            "Cost changes with which labs are ordered, where bloodwork is drawn, and whether ultrasound is billed by the clinic.",
        },
        amount("female-workup"),
      ),
    );
  }

  if (diagnosticProfile === "male" || diagnosticProfile === "couple") {
    beforeCycleLines.push(
      toLine(
        {
          key: "male-workup",
          label: "Semen analysis and partner labs",
          status: "often-separate",
          multiplier: "once",
          defaultSelected:
            treatment === "embryo_freezing" ||
            treatment === "ivf" ||
            treatment === "ivf_icsi" ||
            treatment === "iui",
          detail:
            "Cost changes with semen analysis type, infectious-disease labs, partner insurance, and whether labs bill separately.",
        },
        amount("male-workup"),
      ),
    );
  }

  if (treatment !== "egg_freezing" && treatment !== "sperm_freezing") {
    beforeCycleLines.push(
      toLine(
        {
          key: "uterine-cavity",
          label: "HSG, SIS, or HyCoSy cavity check",
          status: "optional",
          multiplier: "once",
          defaultSelected: treatment === "ivf" || treatment === "ivf_icsi",
          detail:
            "Cost changes by test type, imaging center, contrast, radiology read, and facility fee.",
        },
        amount("uterine-cavity"),
      ),
    );
  }

  pushSection({
    key: "before-cycle",
    title: "Before cycle",
    description: "Testing and planning costs that can appear before treatment.",
    lines: beforeCycleLines,
  });

  const cycleLines: (EstimateScenarioLine | null)[] = [];
  if (treatment === "egg_freezing") {
    cycleLines.push(
      toLine(
        {
          key: "egg-freezing-cycle",
          label: "Egg freezing cycle package",
          status: "likely-included",
          multiplier: "cycle",
          defaultSelected: true,
          detail:
            "Ask whether medications, anesthesia, storage, monitoring visits, and outside labs are separate.",
        },
        amount("egg-freezing-cycle"),
      ),
    );
  } else if (treatment === "embryo_freezing") {
    cycleLines.push(
      toLine(
        {
          key: "embryo-freezing-cycle",
          label: "Embryo freezing cycle package",
          status: "likely-included",
          multiplier: "cycle",
          defaultSelected: true,
          detail:
            "Ask whether fertilization, culture length, freezing, anesthesia, storage, and lab add-ons are separate.",
        },
        amount("embryo-freezing-cycle"),
      ),
    );
  } else if (treatment === "ivf" || treatment === "ivf_icsi") {
    cycleLines.push(
      toLine(
        {
          key: "ivf-cycle",
          label: "IVF retrieval and lab cycle",
          status: "likely-included",
          multiplier: "cycle",
          defaultSelected: true,
          detail:
            "Ask whether medications, anesthesia, embryo freezing, storage, genetic testing, and transfer are separate.",
        },
        amount("ivf-cycle"),
      ),
    );
  } else if (treatment === "fet") {
    cycleLines.push(
      toLine(
        {
          key: "fet-cycle",
          label: "Frozen embryo transfer cycle",
          status: "likely-included",
          multiplier: "transfer",
          defaultSelected: true,
          detail:
            "Ask whether monitoring, thaw, embryo handling, transfer meds, and outside labs are separate.",
        },
        amount("fet-cycle"),
      ),
    );
  } else if (treatment === "iui") {
    cycleLines.push(
      toLine(
        {
          key: "iui-cycle",
          label: "Monitored IUI cycle",
          status: "likely-included",
          multiplier: "cycle",
          defaultSelected: true,
          detail:
            "Ask whether monitoring, bloodwork, trigger shot, sperm prep, and donor sperm are separate.",
        },
        amount("iui-cycle"),
      ),
    );
  } else if (treatment === "ovulation_induction") {
    cycleLines.push(
      toLine(
        {
          key: "oi-cycle",
          label: "Ovulation induction monitoring",
          status: "likely-included",
          multiplier: "cycle",
          defaultSelected: true,
          detail:
            "Cost changes with monitoring visit count, bloodwork frequency, trigger medication, and pharmacy billing.",
        },
        amount("oi-cycle"),
      ),
    );
  } else if (treatment === "sperm_freezing") {
    cycleLines.push(
      toLine(
        {
          key: "sperm-freezing-cycle",
          label: "Sperm freezing intake and cryopreservation",
          status: "likely-included",
          multiplier: "cycle",
          defaultSelected: true,
          detail:
            "Ask whether analysis, infectious labs, freezing, storage, and future shipping are separate.",
        },
        amount("sperm-freezing-cycle"),
      ),
    );
  }

  if (isRetrievalTreatment(treatment)) {
    cycleLines.push(
      toLine(
        {
          key: "anesthesia",
          label: "Retrieval anesthesia",
          status: "often-separate",
          multiplier: "cycle",
          defaultSelected: true,
          detail:
            "Cost changes if anesthesia bills through a separate group, facility, or insurance claim.",
        },
        amount("anesthesia"),
      ),
    );
  }

  pushSection({
    key: "cycle",
    title: "Cycle and procedure",
    description: "The main clinical package for the selected treatment.",
    lines: cycleLines,
  });

  const medicationLineId = getMedicationLineId(treatment);
  const medicationBase =
    medicationLineId != null ? amount(medicationLineId) : null;
  if (medicationLineId != null && medicationBase != null) {
    pushSection({
      key: "medications",
      title: "Medications",
      description:
        "Your protocol, pharmacy, insurance, and savings programs can move this number a lot.",
      lines: [
        toLine(
          {
            key: "medications",
            label:
              treatment === "fet"
                ? "Transfer preparation medications"
                : treatment === "iui"
                  ? "IUI medications"
                  : treatment === "ovulation_induction"
                    ? "Ovulation induction medications"
                    : "Stimulation medications",
            status: "often-separate",
            multiplier: treatment === "fet" ? "transfer" : "cycle",
            defaultSelected: true,
            detail:
              medicationIntensity === "high"
                ? "Higher-dose plans can need more FSH/LH, antagonist, trigger, and refill orders."
                : medicationIntensity === "low"
                  ? "Lower-intensity plans may use fewer injections, but brand, pharmacy, and discounts still matter."
                  : "Cost changes with dose, brand, pharmacy, insurance coverage, cash pricing, and savings-program eligibility.",
          },
          roundModeledEstimate(
            medicationBase * getMedicationFactor(medicationIntensity),
          ),
        ),
      ],
    });
  }

  const labLines: (EstimateScenarioLine | null)[] = [];
  if (hasEmbryoLabPath(treatment)) {
    labLines.push(
      toLine(
        {
          key: "icsi",
          label: "ICSI fertilization",
          status: treatment === "ivf_icsi" ? "likely-included" : "optional",
          multiplier: "cycle",
          defaultSelected: treatment === "ivf_icsi",
          detail:
            "Cost changes if ICSI is priced per cycle, per egg, or already bundled into the IVF package.",
        },
        amount("icsi"),
      ),
    );
    labLines.push(
      toLine(
        {
          key: "pgta",
          label: "PGT-A biopsy, shipping, and lab testing",
          status: "optional",
          multiplier: "embryo",
          defaultSelected: false,
          detail:
            "Cost changes with embryo count, biopsy fee, shipping, genetic lab pricing, and counseling.",
        },
        amount("pgta"),
      ),
    );
    labLines.push(
      toLine(
        {
          key: "pgtm-sr",
          label: "PGT-M or PGT-SR setup and testing",
          status: "optional",
          multiplier: "once",
          defaultSelected: false,
          detail:
            "Used for a known inherited condition or chromosome rearrangement; cost changes with test setup and family samples.",
        },
        amount("pgtm-sr"),
      ),
    );
    labLines.push(
      toLine(
        {
          key: "pgtp",
          label: "PGT-P polygenic risk screening",
          status: "optional",
          multiplier: "embryo",
          defaultSelected: false,
          detail:
            "Not offered by every clinic or lab; cost changes with embryo count and the specific screening panel.",
        },
        amount("pgtp"),
      ),
    );
    labLines.push(
      toLine(
        {
          key: "assisted-hatching",
          label: "Assisted hatching or embryo prep add-on",
          status: "optional",
          multiplier: "cycle",
          defaultSelected: false,
          detail:
            "Cost changes if the clinic recommends it selectively, bundles it, or prices embryo prep separately.",
        },
        amount("assisted-hatching"),
      ),
    );
  }

  if (treatment === "iui") {
    labLines.push(
      toLine(
        {
          key: "sperm-wash",
          label: "Sperm wash/preparation",
          status: "often-separate",
          multiplier: "cycle",
          defaultSelected: true,
          detail:
            "Cost changes with wash method, sperm source, same-day lab timing, and andrology billing.",
        },
        amount("sperm-wash"),
      ),
    );
  }

  pushSection({
    key: "lab-add-ons",
    title: "Lab add-ons",
    description: "Embryology or sperm-lab services that vary by treatment.",
    lines: labLines,
  });

  if (hasDonorOptionsPath(treatment)) {
    const donorLines: (EstimateScenarioLine | null)[] = [];

    if (
      treatment === "iui" ||
      treatment === "embryo_freezing" ||
      treatment === "ivf" ||
      treatment === "ivf_icsi"
    ) {
      donorLines.push(
        toLine(
          {
            key: "donor-sperm",
            label: "Donor sperm vial, shipping, and handling",
            status: "optional",
            multiplier: treatment === "iui" ? "cycle" : "once",
            defaultSelected: false,
            detail:
              "Cost changes with sperm bank, vial count, donor type, genetic screening, shipping, and storage.",
          },
          amount("donor-sperm"),
        ),
      );
    }

    if (
      treatment === "embryo_freezing" ||
      treatment === "ivf" ||
      treatment === "ivf_icsi"
    ) {
      donorLines.push(
        toLine(
          {
            key: "donor-eggs",
            label: "Donor eggs or donor egg cycle",
            status: "optional",
            multiplier: "once",
            defaultSelected: false,
            detail:
              "Cost changes with frozen eggs vs fresh donor, egg bank fees, donor compensation, agency fees, and legal work.",
          },
          amount("donor-eggs"),
        ),
      );
    }

    if (treatment === "fet") {
      donorLines.push(
        toLine(
          {
            key: "donor-embryo",
            label: "Donor embryo coordination",
            status: "optional",
            multiplier: "once",
            defaultSelected: false,
            detail:
              "Cost changes with matching program, legal review, embryo shipping, receiving-clinic fees, and required screening.",
          },
          amount("donor-embryo"),
        ),
      );
    }

    donorLines.push(
      toLine(
        {
          key: "known-donor-coordination",
          label: "Known donor legal and screening",
          status: "optional",
          multiplier: "once",
          defaultSelected: false,
          detail:
            "Cost changes with attorney requirements, donor testing, psych consults, FDA screening, and clinic coordination.",
        },
        amount("known-donor-coordination"),
      ),
    );

    pushSection({
      key: "donor-options",
      title: "Donor options",
      description: "Sperm, eggs, embryos, and known-donor steps if they apply.",
      lines: donorLines,
    });
  }

  const storageLines: (EstimateScenarioLine | null)[] = [];
  if (
    treatment === "egg_freezing" ||
    treatment === "embryo_freezing" ||
    treatment === "ivf" ||
    treatment === "ivf_icsi" ||
    treatment === "sperm_freezing"
  ) {
    storageLines.push(
      toLine(
        {
          key: "storage",
          label:
            treatment === "sperm_freezing"
              ? "Sperm storage"
              : treatment === "egg_freezing"
                ? "Egg storage"
                : "Embryo storage",
          status: "often-separate",
          multiplier: "storage-year",
          defaultSelected: true,
          detail:
            "Cost changes with first-year inclusion, specimen count, storage vendor, and annual renewal price.",
        },
        amount("storage"),
      ),
    );
    storageLines.push(
      toLine(
        {
          key: "specimen-transfer",
          label: "Future specimen transfer or shipping",
          status: "optional",
          multiplier: "once",
          defaultSelected: false,
          detail:
            "Cost changes with transfer-out paperwork, courier type, distance, and receiving-clinic fees.",
        },
        amount("specimen-transfer"),
      ),
    );
  }

  pushSection({
    key: "storage",
    title: "Storage",
    description: "Recurring cryostorage and future transport considerations.",
    lines: storageLines,
  });

  if (hasTransferPath(treatment)) {
    pushSection({
      key: "transfer-path",
      title: "Transfer path",
      description: "Costs after embryos exist and a transfer is planned.",
      lines: [
        toLine(
          {
            key: "additional-fet",
            label:
              treatment === "fet"
                ? "Additional transfer cycle"
                : "Frozen transfer",
            status: treatment === "fet" ? "optional" : "often-separate",
            multiplier: "transfer",
            defaultSelected: treatment !== "fet",
            detail:
              "Cost changes if transfer, thaw, monitoring, embryo handling, and procedure fee are billed separately.",
          },
          amount("additional-fet"),
        ),
        toLine(
          {
            key: "transfer-meds",
            label: "Transfer medications",
            status: "often-separate",
            multiplier: "transfer",
            defaultSelected: treatment !== "fet",
            detail:
              "Cost changes with progesterone route, estrogen plan, cycle length, pharmacy, and insurance coverage.",
          },
          amount("transfer-meds"),
        ),
      ],
    });
  }

  const extraCycleBaseLineId: EstimateLineId =
    treatment === "egg_freezing"
      ? "egg-freezing-cycle"
      : treatment === "iui"
        ? "iui-cycle"
        : treatment === "ovulation_induction"
          ? "oi-cycle"
          : "ivf-cycle";
  const extraCycleBase = amount(extraCycleBaseLineId);
  const extraCycleAmount =
    extraCycleBase == null ? null : extraCycleBase + (medicationBase ?? 0);
  pushSection({
    key: "future-scenarios",
    title: "Future scenarios",
    description: "Not always part of today's bill, but useful for budgeting.",
    lines: [
      toLine(
        {
          key: "extra-cycle-buffer",
          label:
            treatment === "egg_freezing"
              ? "Possible additional freezing round"
              : treatment === "iui" || treatment === "ovulation_induction"
                ? "Possible additional cycle"
                : "Possible additional retrieval cycle",
          status: "optional",
          multiplier: "once",
          defaultSelected: false,
          detail:
            "Adds a budget scenario for another attempt; exact cost depends on what can be reused.",
        },
        extraCycleAmount,
      ),
    ],
  });

  return sections;
}
